import { createSelector } from '@reduxjs/toolkit';

import { VIRUS_SCAN_STATUS } from '@app/components/Media/constants';

export const keyboardShortcuts = ({
  key,
  mediaIndex,
  mediaCount,
  changeMediaIndex,
  close,
}) => {
  if (key === 'ArrowLeft' && mediaIndex > 0) {
    changeMediaIndex(mediaIndex - 1);
  } else if (key === 'ArrowRight' && mediaIndex + 1 < mediaCount) {
    changeMediaIndex(mediaIndex + 1);
  } else if (key === 'Escape') {
    close();
  }
};

// Generic media type constants
const GENERIC_CONTENT_TYPES = ['text/x-vcard', 'application/pdf'];
const GENERIC_CONTENT_TYPE_PREFIXES = ['text/', 'application/'];

// Helper to determine media type from content type
export const getMediaType = (contentType) => {
  if (!contentType) return 'unknown';

  const normalizedContentType = contentType.toLowerCase();
  const type = normalizedContentType.split('/')[0];

  // Standard media types
  if (type === 'image' || type === 'video' || type === 'audio') {
    return type;
  }

  // Generic media types that need custom rendering
  if (
    GENERIC_CONTENT_TYPES.includes(normalizedContentType) ||
    GENERIC_CONTENT_TYPE_PREFIXES.some((prefix) =>
      normalizedContentType.startsWith(prefix),
    )
  ) {
    return 'generic';
  }

  return 'unknown';
};

// Memoized selector for the active chat's visible media (redacted and
// non-clean items filtered out, sorted oldest -> newest). Memoization keeps the
// returned array referentially stable between renders as long as the source
// state slices are unchanged, which is required so that effects depending on it
// don't re-fire on every store update.
//
// Redaction state is read from BOTH jobs.activeChat.media (the persisted flag)
// AND the `media` slice. REDACT_IMAGE only updates the `media` slice during a
// session, so cross-referencing it is what keeps a just-redacted image out of
// full-screen mode instead of leaving it visible/navigable.
const selectActiveChatRawMedia = (state) => state?.jobs?.activeChat?.media;
const selectMediaRedactionState = (state) => state?.media;

export const selectVisibleActiveChatMedia = createSelector(
  [selectActiveChatRawMedia, selectMediaRedactionState],
  (media, mediaState) =>
    (
      media?.filter((item) => {
        const isRedacted =
          item?.isImageRedacted ||
          mediaState?.[item?.fileUrl]?.isImageRedacted === true;
        return !isRedacted && item?.virusScanStatus === VIRUS_SCAN_STATUS.CLEAN;
      }) || []
    ).sort((a, b) => {
      const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateA - dateB;
    }),
);

// Clamp the redux media index to the current list bounds for RENDERING. When
// the visible list shrinks (e.g. an in-session redaction) the stored index can
// briefly be out of range; selecting with the raw index would yield an
// undefined item and unmount the viewer for a frame before the clamp effect
// corrects the store. Returns 0 for an empty list (caller then closes).
export const getRenderIndex = (selectedIndex, count) => {
  if (count <= 0) return 0;
  return Math.min(Math.max(selectedIndex, 0), count - 1);
};

// Whether the cached media is safe to render for the current item. useFileCache
// keeps the previous file's object URL until the next one loads (and on error),
// so a slide must only render when the loaded blob actually belongs to the
// current item. Otherwise a just-redacted image could flash in a neighbor's
// slot during navigation/redaction.
export const isMediaReadyForItem = ({
  loadingState,
  localFileUrl,
  loadedFileUrl,
  currentFileUrl,
}) =>
  !!localFileUrl &&
  !!currentFileUrl &&
  loadingState !== 'loading' &&
  loadedFileUrl === currentFileUrl;

// Decide how the lightbox should keep its selection valid after the visible
// media list changes (e.g. an image was redacted and filtered out). Returns:
//   { type: 'close' }                 - nothing left, close the modal
//   { type: 'clamp', index: <n> }     - index past the end, move to last
//   { type: 'navigate', index: <n> }  - the shown item was removed mid-list,
//                                        step back to the previous image
//   { type: 'none' }                  - selection still valid, do nothing
// `currentRemoved` means the item that was on screen is no longer in the list
// (it was redacted/removed), as opposed to plain navigation. Only emitting an
// action when something must change avoids the no-op dispatch loop that
// triggered "Maximum update depth exceeded".
export const getMediaIndexAction = ({
  count,
  selectedIndex,
  currentRemoved = false,
}) => {
  if (count === 0) {
    return { type: 'close' };
  }
  if (selectedIndex >= count) {
    return { type: 'clamp', index: count - 1 };
  }
  if (currentRemoved && selectedIndex > 0) {
    return { type: 'navigate', index: selectedIndex - 1 };
  }
  return { type: 'none' };
};
